import type { InputHTMLAttributes, Ref } from 'react'
import './money-input.css'

/**
 * A figure in dollars, with the sign INSIDE the control.
 *
 * The sign is part of the field rather than a character beside it: set outside,
 * the row reads as a symbol and a shorter box, and the box no longer lines up
 * with whatever sits above it. It is decoration over the field's own leading
 * padding and takes no clicks, so the whole row still focuses the input.
 *
 * The value is the string somebody typed, never cents — `centsFrom` is what
 * turns it into money, and it is the caller's to apply, because what an empty
 * field or a typo MEANS differs per form.
 */
export function MoneyInput({
  className,
  ref,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  ref?: Ref<HTMLInputElement>
}) {
  return (
    <span className={`money-input ${className ?? ''}`}>
      <span className="money-input__sign" aria-hidden>
        $
      </span>
      <input
        className="control money-input__field"
        type="number"
        inputMode="decimal"
        /* Cents are a legal figure here, so the step is a cent: at the browser
           default of 1 every amount with cents in it is a step mismatch, and
           the arrow keys move by whole dollars. A caller states `min`/`max`
           from the bounds the server enforces, so the constraint is in the
           control rather than only in the prose beside it. */
        step="0.01"
        ref={ref}
        {...rest}
      />
    </span>
  )
}
